/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState, useMemo, useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { DocumentationView } from "../DocumentationView";
import { MdxCommand } from "./MdxCommand";
import { MdxLink } from "./MdxLink";
import { MdxBadge } from "./MdxBadge";
import { DocumentationFlag } from "./DocumentationFlag";
import { DocumentationMetadata } from "./DocumentationMetadata";

export interface IDocumentationContentProps {
	view: DocumentationView;
}

export const DocumentationContent: React.FC<IDocumentationContentProps> = ({ view }) => {
	const baseClass = BEM("edt-documentation-content");
	baseClass.add("edt-base");
	const [selectedPath, setSelectedPath] = useState<string | null>(view.getSelected());

	// Update local state when selection changes
	useSimpleEvent(view.onSelectedChange, () => {
		setSelectedPath(view.getSelected());
	}, [view], true);

	// Create link component with view context
	const LinkComponent = useCallback(
		(props: { href?: string; children?: React.ReactNode }) => (
			<MdxLink {...props} view={view} />
		),
		[view]
	);

	// MDX components - memoized to prevent unnecessary re-renders
	const components = useMemo(() => ({
		Command: MdxCommand,
		Badge: MdxBadge,
		a: LinkComponent
	}), [LinkComponent]);

	// Get content for selected path using memoization
	const doc = useMemo(() => {
		return view.getContent();
	}, [view, selectedPath]); // selectedPath dependency ensures re-render when selection changes

	if (!doc) {
		return (
			<div className={baseClass.toClassName()}>
				<div className={baseClass.element("empty").toClassName()}>
					<p>No documentation selected</p>
				</div>
			</div>
		);
	}

	const DocComponent = doc.component;
	const flags = doc.metadata.flags as string[] | undefined;

	return (
		<div className={baseClass.toClassName()}>
			{flags && flags.length > 0 && (
				<div className={baseClass.element("flags").toClassName()}>
					{flags.map((flag) => (
						<DocumentationFlag key={flag} flag={flag} />
					))}
				</div>
			)}
			<div className="edt-markdown-content">
				<DocComponent components={components} />
			</div>
			<DocumentationMetadata
				lastModified={doc.metadata.lastModified}
				filePath={doc.filePath}
			/>
		</div>
	);
};
